import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { RecommendationCard } from '../../components/intelligence/RecommendationCard';
import { FilterBar, createFilterState, filtersToParams } from '../../components/intelligence/FilterBar';
import { SkeletonCard } from '../../components/intelligence/SkeletonLoader';
import { Button } from '../../components/ui';

interface Recommendation {
  id: string;
  title: string;
  category: 'growth' | 'product' | 'infra';
  impact: string;
  risk: 'low' | 'medium' | 'high';
  rollout: string;
  description: string;
}

const loading = signal(true);
const error = signal('');
const recs = signal<Recommendation[]>([]);
const filters = createFilterState();
const filterCat = signal<string>('');

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    const res = await api.get<{ insights: any[] }>(`/intelligence/insights?type=recommendation&${params.toString()}`);
    recs.value = (res.insights || []).map((r: any) => ({
      id: r.id,
      title: r.title || r.insight_text || '',
      category: r.category || 'product',
      impact: r.impact || 'médio',
      risk: r.risk || 'medium',
      rollout: r.rollout || 'gradual',
      description: r.evidence || r.description || '',
    }));
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar recomendações';
    recs.value = [];
  } finally {
    loading.value = false;
  }
}

export function RecommendationsPanel() {
  useEffect(() => { loadData(); }, []);

  const filtered = filterCat.value ? recs.value.filter((r) => r.category === filterCat.value) : recs.value;

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Recomendações</h2>
      <FilterBar filters={filters} onApply={loadData} showTool={false} />

      <div class="flex gap-2 mb-4">
        {[
          { v: '', label: 'Todas' },
 { v: 'growth', label: 'Crescimento' },
 { v: 'product', label: 'Produto' },
 { v: 'infra', label: 'Infra' },
        ].map(({ v, label }) => (
          <Button variant="ghost"
            key={v}
            class={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filterCat.value === v ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => { filterCat.value = v; }}
          >
            {label}
          </Button>
        ))}
      </div>

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading.value ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={4} />)
        ) : filtered.length === 0 ? (
          <div class="col-span-full text-gray-500 text-center py-12">Nenhuma recomendação</div>
        ) : (
          filtered.map((r) => (
            <RecommendationCard key={r.id} {...r} />
          ))
        )}
      </div>
    </div>
  );
}
